import { createContext, useContext, useMemo, useReducer } from "react";
import { logDebug } from "helpers/log-helper/log-helper";
import { MenuService } from "services/Menu.service";
import { UpsellGroup } from "types/upsellGroup";
import { Article } from "types/article";
import { LogPrefix } from "types/logging";

interface IArticleStore {
	/**
	 * Current article to display details for in ui
	 */
	readonly article: Article | undefined;
	/**
	 * Is currently fetching article
	 */
	readonly isLoadingArticle: boolean;
	/**
	 * Has finished fetch request for article,
	 * true no matter if the response was an error or success
	 */
	readonly hasLoadedArticle: boolean;
	/**
	 * Upsell groups associated with a given article
	 */
	readonly upsellGroups: UpsellGroup[];
	/**
	 * Is currently fetching upsell groups
	 */
	readonly isLoadingUpsellGroups: boolean;
	/**
	 * Has finished fetch request for upsell groups,
	 * true no matter if the response was an error or success
	 */
	readonly hasLoadedUpsellGroups: boolean;
}

interface IArticleAPI {
	/**
	 * Fetch article with id, associated with menu id
	 */
	getArticle: (menuId: string, articleId: string, cartId: string | undefined) => Promise<Article | undefined>;
	/**
	 * Fetch upsell groups for given article, associated with menu id
	 */
	getUpsellGroups: (menuId: string, articleId: string, cartId: string | undefined) => Promise<UpsellGroup[]>;
}

const initialState: IArticleStore = {
	article: undefined,
	isLoadingArticle: false,
	hasLoadedArticle: false,
	upsellGroups: [],
	isLoadingUpsellGroups: false,
	hasLoadedUpsellGroups: false,
};

enum ActionType {
	SET_ARTICLE,
	START_LOADING_ARTICLE,
	STOP_LOADING_ARTICLE,
	SET_UPSELL_GROUPS,
	START_LOADING_UPSELL_GROUPS,
	STOP_LOADING_UPSELL_GROUPS,
}

type Action =
	| { type: ActionType.SET_ARTICLE; payload: Article | undefined }
	| { type: ActionType.START_LOADING_ARTICLE }
	| { type: ActionType.STOP_LOADING_ARTICLE }
	| { type: ActionType.SET_UPSELL_GROUPS; payload: UpsellGroup[] }
	| { type: ActionType.START_LOADING_UPSELL_GROUPS }
	| { type: ActionType.STOP_LOADING_UPSELL_GROUPS };

const ArticleAPI = createContext<IArticleAPI>({} as IArticleAPI);
const ArticleStore = createContext<IArticleStore>({} as IArticleStore);

const reducer = (state: IArticleStore, action: Action): IArticleStore => {
	switch (action.type) {
		case ActionType.SET_ARTICLE:
			return {
				...state,
				article: action.payload,
				isLoadingArticle: false,
				hasLoadedArticle: true,
			};
		case ActionType.START_LOADING_ARTICLE:
			return {
				...state,
				isLoadingArticle: true,
				hasLoadedArticle: false,
			};
		case ActionType.STOP_LOADING_ARTICLE:
			return {
				...state,
				isLoadingArticle: false,
				hasLoadedArticle: true,
			};
		case ActionType.SET_UPSELL_GROUPS:
			return {
				...state,
				upsellGroups: action.payload,
				isLoadingUpsellGroups: false,
				hasLoadedUpsellGroups: true,
			};
		case ActionType.START_LOADING_UPSELL_GROUPS:
			return {
				...state,
				isLoadingUpsellGroups: true,
				hasLoadedUpsellGroups: false,
			};
		case ActionType.STOP_LOADING_UPSELL_GROUPS:
			return {
				...state,
				isLoadingUpsellGroups: false,
				hasLoadedUpsellGroups: true,
			};

		default:
			return state;
	}
};

export function ArticleProvider({
	children,
	values = initialState,
}: {
	children: React.ReactNode;
	/** Initial state values, used for testing. */
	values?: IArticleStore;
}) {
	const [state, dispatch] = useReducer(reducer, values);

	const api: IArticleAPI = useMemo(() => {
		const getUpsellGroups = async (menuId: string, articleId: string, cartId?: string) => {
			// Prevent previously stored upsell groups
			// from appearing in the ui by resetting the article state immedietly
			dispatch({ type: ActionType.SET_UPSELL_GROUPS, payload: [] });

			try {
				dispatch({ type: ActionType.START_LOADING_UPSELL_GROUPS });
				const { upsellGroups } = await MenuService.getMenuArticleUpsellGroups(menuId, articleId, cartId);
				dispatch({ type: ActionType.SET_UPSELL_GROUPS, payload: upsellGroups });
				return upsellGroups;
			} catch (error: unknown) {
				dispatch({ type: ActionType.STOP_LOADING_UPSELL_GROUPS });
				if (error instanceof Error && error.name === "AbortError") {
					// Expected when clicking on multiple articles, not letting requests finish
					logDebug(LogPrefix.Article, "Aborted request for Upsell Groups");
					return [];
				}
				throw error;
			}
		};

		const getArticle = async (menuId: string, articleId: string, cartId?: string) => {
			// Prevent previously stored article from appearing in the ui by resetting the article state immedietly
			dispatch({ type: ActionType.SET_ARTICLE, payload: undefined });

			try {
				dispatch({ type: ActionType.START_LOADING_ARTICLE });
				const article = await MenuService.getMenuArticle(menuId, articleId, cartId);

				dispatch({ type: ActionType.SET_ARTICLE, payload: article });
				return article;
			} catch (error: unknown) {
				dispatch({ type: ActionType.STOP_LOADING_ARTICLE });

				if (error instanceof Error && error.name === "AbortError") {
					// Expected when clicking on multiple articles, not letting requests finish
					logDebug(LogPrefix.Article, "Aborted request for Article");
					return undefined;
				}

				// Error log is handled further down in ui
				throw error;
			}
		};

		return { getArticle, getUpsellGroups };
	}, []);

	return (
		<ArticleAPI.Provider value={api}>
			<ArticleStore.Provider value={state}>{children}</ArticleStore.Provider>
		</ArticleAPI.Provider>
	);
}

export const useArticleStore = (): IArticleStore => useContext(ArticleStore);
export const useArticleAPI = (): IArticleAPI => useContext(ArticleAPI);
